import { useState } from 'react'
import { GoogleOAuthProvider } from '@react-oauth/google'
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Container,
  Alert,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  useTheme,
  Divider
} from '@mui/material'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useNavigate, useLocation } from 'react-router-dom'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import LanguageIcon from '@mui/icons-material/Language'
import GoogleLoginButton from '../components/auth/GoogleLoginButton'

const Login = () => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  const theme = useTheme()
  const { login, loginWithGoogle } = useAuth()
  const { lang, setLang, t } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()

  const handleGoogleSuccess = async (credential: string) => {
    setError(null)
    setIsSubmitting(true)

    try {
      console.log('🔑 Login.tsx: Google login attempt')
      const result = await loginWithGoogle(credential)
      console.log('📥 Login.tsx: Google login result:', result)

      if (result.success) {
        console.log('✅ Login.tsx: Google login successful, redirecting...')
        const from = (location.state as any)?.from?.pathname || '/modes'
        navigate(from, { replace: true })
      } else {
        console.log('❌ Login.tsx: Google login failed:', result.message)
        setError(result.message || t('login.error.google'))
      }
    } catch (err: any) {
      console.error('❌ Login.tsx: Google login exception:', err)
      setError(t('login.error.google'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleGoogleError = (error: string) => {
    console.error('❌ Login.tsx: Google error:', error)
    setError(t('login.error.google'))
  }

  // Feature flag: allow disabling Google login via build-time env
  // DISABLED: Google login temporarily disabled (code preserved for future use)
  const googleLoginEnabled = (import.meta.env.VITE_ENABLE_GOOGLE_LOGIN ?? 'false') !== 'false'
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!username || !password) {
      setError(t('login.error.required'))
      return
    }

    setError(null)
    setIsSubmitting(true)

    try {
      console.log('🔑 Login.tsx: Submitting login for', username)
      const result = await login(username, password)
      console.log('📥 Login.tsx: Login result:', result)

      if (result.success) {
        console.log('✅ Login.tsx: Login successful, redirecting...')
        // Redirect to the page user was trying to access, or mode selection page
        const from = (location.state as any)?.from?.pathname || '/modes'
        console.log('🔀 Login.tsx: Redirecting to:', from)
        navigate(from, { replace: true })
      } else {
        console.log('❌ Login.tsx: Login failed:', result.message)
        setError(t('login.error.credentials'))
      }
    } catch (err) {
      console.error('❌ Login.tsx: Login error:', err)
      setError(t('login.error.general'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleLanguage = () => {
    setLang(lang === 'pl' ? 'en' : 'pl')
  }

  const handleClickShowPassword = () => {
    setShowPassword((show) => !show)
  }
  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: 2,
      }}
    >
      {/* Language selector */}
      <IconButton 
        sx={{ 
          position: 'absolute', 
          top: 20, 
          right: 20,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
          }
        }}
        onClick={toggleLanguage}
      >
        <LanguageIcon />
      </IconButton>
      
      {/* Decorative pattern elements */}
      <Box 
        sx={{ 
          position: 'absolute',
          top: -100,
          left: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(74, 105, 187, 0.1) 0%, rgba(74, 105, 187, 0.05) 70%, rgba(74, 105, 187, 0) 100%)',
        }}
      />
      
      <Box 
        sx={{ 
          position: 'absolute',
          bottom: -150,
          right: -150,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99, 132, 224, 0.1) 0%, rgba(99, 132, 224, 0.05) 70%, rgba(99, 132, 224, 0) 100%)',
        }}
      />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper 
          elevation={4}
          sx={{ 
            p: 0,
            borderRadius: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '800px',
            margin: 'auto',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)'
          }}
        >
          {/* Login form */}
          <Box sx={{ 
            width: '100%',
            px: { xs: 3, sm: 5, md: 6 }, 
            py: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            background: 'white'
          }}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: theme.palette.primary.main,
                mb: 1,
                letterSpacing: '-0.01em'
              }}
            >
              {t('login.title')}
            </Typography>

            <Typography variant="subtitle1" sx={{ mb: 4, color: 'text.secondary' }}>
              {t('login.signInPrompt')}
            </Typography>
            
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  borderRadius: 2, 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                {error}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={t('login.username')}
                variant="outlined"
                margin="normal"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                fullWidth
                label={t('login.password')}
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      sx={{
                        color: theme.palette.primary.main,
                        '&.Mui-checked': {
                          color: theme.palette.primary.main,
                        }
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('login.remember')}</Typography>}
                />

                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.primary.main,
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {t('login.forgot')}
                </Typography>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isSubmitting}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(74, 105, 187, 0.2)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(74, 105, 187, 0.3)',
                  }
                }}
              >
                {isSubmitting ? t('login.loggingIn') : t('login.submit')}
              </Button>

              {googleLoginEnabled && (
                <>
                  <Divider sx={{ my: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('login.or')}
                    </Typography>
                  </Divider>
                  <GoogleLoginButton
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                  />
                </>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''
const GOOGLE_LOGIN_ENABLED = (import.meta.env.VITE_ENABLE_GOOGLE_LOGIN ?? 'false') !== 'false'

const LoginWithProvider = () => {
  if (GOOGLE_LOGIN_ENABLED && GOOGLE_CLIENT_ID) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <Login />
      </GoogleOAuthProvider>
    )
  }
  return <Login />
}

export default LoginWithProvider
